// Lakai size preference helper.
// Single source of truth for the session-only size-filter contract used by the
// homepage filter, collection filter, floating pill, and PDP default selection.
(function () {
  'use strict';
  if (window.P7SizePrefs) return;

  var STORAGE_KEY = 'p7_size_prefs_session';
  var MEMORY_KEY = 'p7_size_memory_session';
  var LEGACY_COOKIE = 'p7_size_prefs';
  var LEGACY_SHOE_SIZE_COOKIE = 'p7_shoe_size';
  var EVENT = 'p7:size-prefs-change';
  var MEMORY_EVENT = 'p7:size-prefs-memory-change';
  var memoryPrefs = null;
  var memoryEnabledFallback = true;
  var US_TIMEZONES = [
    'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville',
    'America/Kentucky/Monticello', 'America/Indiana/Indianapolis',
    'America/Indiana/Vincennes', 'America/Indiana/Winamac',
    'America/Indiana/Marengo', 'America/Indiana/Petersburg',
    'America/Indiana/Vevay', 'America/Indiana/Tell_City', 'America/Indiana/Knox',
    'America/Chicago', 'America/Menominee', 'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem', 'America/North_Dakota/Beulah',
    'America/Denver', 'America/Boise', 'America/Phoenix', 'America/Los_Angeles',
    'America/Anchorage', 'America/Juneau', 'America/Sitka', 'America/Metlakatla',
    'America/Yakutat', 'America/Nome', 'America/Adak', 'Pacific/Honolulu',
  ];

  function deleteCookie(name) {
    document.cookie = name + '=; path=/; max-age=0; SameSite=Lax';
  }

  function readSession() {
    try {
      return window.sessionStorage ? window.sessionStorage.getItem(STORAGE_KEY) : memoryPrefs;
    } catch (err) {
      return memoryPrefs;
    }
  }

  function writeSession(value) {
    memoryPrefs = value;
    try {
      if (window.sessionStorage) window.sessionStorage.setItem(STORAGE_KEY, value);
    } catch (err) {
      /* best-effort fallback */
    }
  }

  function deleteSession() {
    memoryPrefs = null;
    try {
      if (window.sessionStorage) window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      /* best-effort fallback */
    }
  }

  function memoryEnabled() {
    try {
      if (window.sessionStorage) {
        return window.sessionStorage.getItem(MEMORY_KEY) !== 'off';
      }
    } catch (err) {
      /* best-effort fallback */
    }
    return memoryEnabledFallback !== false;
  }

  function writeMemoryEnabled(enabled) {
    memoryEnabledFallback = enabled !== false;
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem(MEMORY_KEY, memoryEnabledFallback ? 'on' : 'off');
      }
    } catch (err) {
      /* best-effort fallback */
    }
  }

  function deleteLegacyStorage() {
    deleteCookie(LEGACY_COOKIE);
    deleteCookie(LEGACY_SHOE_SIZE_COOKIE);
    try {
      if (window.localStorage) {
        window.localStorage.removeItem(LEGACY_COOKIE);
        window.localStorage.removeItem(LEGACY_SHOE_SIZE_COOKIE);
      }
    } catch (err) {
      /* best-effort cleanup */
    }
  }

  function sameSize(a, b) {
    return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
  }

  function includes(list, size) {
    return list.some(function (item) { return sameSize(item, size); });
  }

  function remove(list, size) {
    return list.filter(function (item) { return !sameSize(item, size); });
  }

  function normalizeList(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    list.forEach(function (item) {
      var value = String(item == null ? '' : item).trim();
      if (value && !includes(out, value)) out.push(value);
    });
    return out;
  }

  // Shoe sizes are single-select for the storefront UI; apparel stays multi.
  // Multi-size order behaviour needs a deeper read before we make browsing carry
  // several shoe sizes at once.
  function normalizePrefs(prefs) {
    prefs = prefs && typeof prefs === 'object' ? prefs : {};
    return {
      s: normalizeList(prefs.s).slice(0, 1),
      a: normalizeList(prefs.a),
    };
  }

  function read() {
    if (!memoryEnabled()) return normalizePrefs({ s: [], a: [] });

    var raw = readSession();
    if (!raw) return normalizePrefs({ s: [], a: [] });

    try {
      return normalizePrefs(JSON.parse(raw));
    } catch (err) {
      return normalizePrefs({ s: [], a: [] });
    }
  }

  function isLikelyUS() {
    try {
      var zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      return US_TIMEZONES.indexOf(zone) !== -1;
    } catch (err) {
      return false;
    }
  }

  // A live shoe-size filter changes card behaviour theme-wide (p7-card.js
  // stops auto-opening the size drawer on hover; the card CSS shows a
  // "Change size" hint instead). Stamped here — the single source of truth
  // for the preference — so every consumer reads one class.
  function stampFilterClass(prefs) {
    try {
      document.documentElement.classList.toggle(
        'p7-shoe-size-active',
        !!(prefs && prefs.s && prefs.s.length)
      );
    } catch (err) { /* documentElement always exists; belt and braces */ }
  }

  function write(prefs) {
    var next = normalizePrefs(prefs);
    deleteLegacyStorage();

    if (!memoryEnabled()) {
      deleteSession();
      stampFilterClass(null);
      return normalizePrefs({ s: [], a: [] });
    }

    if (!next.s.length && !next.a.length) {
      deleteSession();
      stampFilterClass(next);
      return next;
    }

    writeSession(JSON.stringify(next));
    stampFilterClass(next);
    return next;
  }

  function emit(detail) {
    document.dispatchEvent(new CustomEvent(EVENT, {
      detail: normalizePrefs(detail || read()),
    }));
  }

  function set(prefs, options) {
    var next = write(prefs);
    if (!options || options.silent !== true) emit(next);
    return next;
  }

  function setMemoryEnabled(enabled, options) {
    writeMemoryEnabled(enabled);
    if (!memoryEnabled()) deleteSession();
    var next = read();
    if (!options || options.silent !== true) {
      emit(next);
      document.dispatchEvent(new CustomEvent(MEMORY_EVENT, {
        detail: { enabled: memoryEnabled(), prefs: next },
      }));
    }
    return memoryEnabled();
  }

  // Tapping the current shoe size clears it; tapping another replaces it.
  // `remove` and `includes` both compare via sameSize (trim + uppercase), so
  // this stays consistent with how every other consumer tests membership.
  function toggleShoe(size, prefs) {
    var next = normalizePrefs(prefs || read());
    var value = String(size || '').trim();
    if (!value) return next;
    next.s = includes(next.s, value) ? remove(next.s, value) : [value];
    return normalizePrefs(next);
  }

  /* ---- size vocabulary ----
     Lives here, not in each consumer, because p7-size-prefs.js is the only
     script on every page and already owns what a size IS. sizeCompare existed
     in three places before this; the homepage and the collection page could
     therefore disagree about ordering, which is exactly the class of drift that
     produced the kids-size bug. */
  var APPAREL_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];

  function isNum(s) { return /^\d+(\.\d+)?$/.test(String(s == null ? '' : s).trim()); }

  // Liquid has no numeric sort for this — `| sort` is lexicographic, so "10"
  // lands before "4". Numeric sizes ascend, then apparel in canonical order,
  // then anything unrecognised alphabetically.
  function sizeCompare(a, b) {
    a = String(a == null ? '' : a).trim();
    b = String(b == null ? '' : b).trim();
    var na = isNum(a), nb = isNum(b);
    if (na && nb) return parseFloat(a) - parseFloat(b);
    if (na) return -1;
    if (nb) return 1;
    var ia = APPAREL_ORDER.indexOf(a.toUpperCase());
    var ib = APPAREL_ORDER.indexOf(b.toUpperCase());
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  }

  // Does a card stocking `cardSizes` satisfy the `selected` filter?
  // No selection means no constraint — every card passes. Several selected
  // sizes are an OR: the shopper wants anything in any of them.
  function matches(cardSizes, selected) {
    if (!Array.isArray(selected) || !selected.length) return true;
    if (!Array.isArray(cardSizes) || !cardSizes.length) return false;
    var mine = cardSizes.map(function (s) {
      return String(s == null ? '' : s).trim().toUpperCase();
    });
    return selected.some(function (sel) {
      return mine.indexOf(String(sel == null ? '' : sel).trim().toUpperCase()) !== -1;
    });
  }

  window.P7SizePrefs = {
    storageKey: STORAGE_KEY,
    cookie: LEGACY_COOKIE,
    shoeCookie: LEGACY_SHOE_SIZE_COOKIE,
    memoryKey: MEMORY_KEY,
    sessionOnly: true,
    event: EVENT,
    memoryEvent: MEMORY_EVENT,
    read: read,
    write: write,
    set: set,
    memoryEnabled: memoryEnabled,
    setMemoryEnabled: setMemoryEnabled,
    emit: emit,
    sameSize: sameSize,
    includes: includes,
    remove: remove,
    toggleShoe: toggleShoe,
    normalize: normalizePrefs,
    isLikelyUS: isLikelyUS,
    isNum: isNum,
    sizeCompare: sizeCompare,
    matches: matches,
  };

  deleteLegacyStorage();
  stampFilterClass(read());
  document.dispatchEvent(new CustomEvent('p7:size-prefs-ready', {
    detail: read(),
  }));
})();
